import type { DeviceType } from './device-type'
import type { Options, SeriesOptionsType } from 'highcharts'

export type Unit = 'duration_in_seconds' | 'euro' | 'percent' | 'raw'

export enum Mode {
  NORMAL = 'normal',
  OVERALL = 'overall',
  OVERVIEW = 'overview',
}

export interface IGetSeriesDataBody {
  app_key: string
  breakdowns: string[]
  device_type: string
  feature_name?: string
  filters: ILookerFilter[]
  kpi_values: IKpiValues
}

export interface IGetSeriesDataResponse {
  data: ISerie[]
}

export interface IGetOverallResponse {
  data: IOverallSerie[] | null
}

export interface IGetOverviewResponse {
  data: IOverviewData[]
}

export interface ILookerFilter {
  condition_type: string
  key: string
  values: string[]
}

export interface IKpiValues {
  field: string
  normalize_by: string[]
  values: string[]
  with_inf?: boolean
  with_sup?: boolean
  with_trend?: boolean
}

export interface ISerie {
  breakdowns?: Record<string, any>
  data: any[][]
  graphType?: string
  name: string
  yAxis?: string
}

interface IOverviewData {
  data: {
    base?: number
    previous?: number
    progress?: number
  }
  name: string
}

export interface IOverallSerie {
  breakdowns?: Record<string, any>
  data: number
  name: string
}

export type GraphConfig = {
  graphType?: any
  overallData?: IOverallSerie[]
  series: ISerie[]
  xAxis: {
    dataType: string
    name?: string
  }
  yAxes: {
    name?: string
    precision: number
    unit: Unit
  }[]
}

export interface IChartSerie {
  colorIndex?: number
  dashStyle?: string
  data?: any[]
  id?: string
  linkedTo?: string
  marker?: {
    enabled?: boolean
    symbol?: string
  }
  name?: string
  opacity?: number
  params?: {
    index: number
    period: number
  }
  pointPlacement?: string | number
  pointRange?: number | null
  type: any
  visible?: boolean
}

export type ExtendedSerie = SeriesOptionsType & IChartSerie

export interface ExtendedHighchartsOptions extends Options {
  series: ExtendedSerie[]
}

export enum CA_PAGES {
  APP_SUMMARY = 'app-summary',
  APP_USAGE = 'app-usage',
  COMMUNITY = 'community',
  CRASH = 'crash',
  MONET_ADS = 'monet-ads',
  MONET_IAP = 'monet-iap',
  OVERVIEW = 'overview',
  OVERVIEW_METRICS = 'overview-metrics',
  OVERVIEW_PUBLISHING = 'overview-publishing',
  SANDBOX = 'sandbox',
  STABILITY = 'stability',
  USAGE = 'usage',
}

export enum BUSINESS_REPORTS_PAGES {
  OVERVIEW = 'business-report-overview',
  SANDBOX = 'business-report-sandbox',
}

export const CA_SECTIONS_LABELS_MAPPING: { [key in CA_PAGES]?: { [index: string]: string } } = {
  [CA_PAGES.OVERVIEW_METRICS]: {
    ADS_MONETISATION: 'Ads Monetisation | Mean ads impressions/revenues number per user',
    APP_USAGE: 'App Usage',
    IAP_MONETISATION: 'IAP Monetisation',
    PREDICTED_LTV_360_PER_USER: 'Predicted LTV 360 per user',
    REVENUES_PER_USERS: 'Revenues per users',
    STABILITY: 'Stability',
    USAGE: 'Usage',
  },
}

/* eslint-disable sort-keys, sort-keys-fix/sort-keys-fix */
export const CA_SECTIONS: Record<CA_PAGES, Record<string, string>> = {
  [CA_PAGES.APP_USAGE]: {},
  [CA_PAGES.APP_SUMMARY]: {
    GENERAL_ANALYTICS: 'general-analytics',
    INSTALLS_OVER_TIME: 'installs-over-time',
  },
  [CA_PAGES.COMMUNITY]: {},
  // NOTE: PAGE.CRASH will be removed once the configs have been replaced by PAGE.STABILITY
  [CA_PAGES.CRASH]: {},
  [CA_PAGES.STABILITY]: {},
  [CA_PAGES.MONET_ADS]: {},
  [CA_PAGES.MONET_IAP]: {},
  [CA_PAGES.OVERVIEW]: {
    REVENUE: 'revenue',
    ADS: 'ads',
    IAP: 'iap',
    USAGE: 'usage',
  },
  [CA_PAGES.OVERVIEW_METRICS]: {
    REVENUES_PER_USERS: 'revenues',
    PREDICTED_LTV_360_PER_USER: 'predicted-ltv',
    ADS_MONETISATION: 'ads-monetisation',
    IAP_MONETISATION: 'iap-monetisation',
    STABILITY: 'stability',
    USAGE: 'usage',
  },
  [CA_PAGES.OVERVIEW_PUBLISHING]: {},
  [CA_PAGES.SANDBOX]: {
    REVENUE: 'revenue',
    ADS: 'ads',
    IAP: 'iap',
    USAGE: 'usage',
  },
  [CA_PAGES.USAGE]: {},
}
/* eslint-enable sort-keys, sort-keys-fix/sort-keys-fix */

export const BUSINESS_REPORTS_SECTIONS: Record<BUSINESS_REPORTS_PAGES, Record<string, string>> = {
  [BUSINESS_REPORTS_PAGES.OVERVIEW]: {},
  [BUSINESS_REPORTS_PAGES.SANDBOX]: {},
}

export interface IBuilderConfigSeries {
  defaultBreakdown?: string
  graphType: string
  minNbu?: number
  normalizedBy: string[]
  values: string[]
  withInf?: boolean
  withSup?: boolean
  withTrend?: boolean
  yAxisName?: string
}

export interface IBuilderConfigYAxis {
  name?: string
  precision: number
  unit: Unit
}

/** @doc Config shared between graph / data / table type */
export interface IBuilderConfig {
  applications: string[]
  column?: number
  featureName?: string
  forceKPIField?: string
  id?: string
  line?: number
  mode: Mode
  nbLastDaysToIgnore: number
  page?: string
  position?: number
  requiredBreakdown?: string
  section?: string
  series: IBuilderConfigSeries[]
  title: string
  tooltip: {
    how?: string
    relatedGlossaryItems: string[]
    what?: string
  }
  wantNegativeEvolution?: boolean
  warningMessage?: string
  yAxes: IBuilderConfigYAxis[]
}

export interface IGraphBuilderConfig extends IBuilderConfig {
  minNbu: number
  withInf: boolean
  withSup: boolean
  withTrend: boolean
}

export interface IDataBuilderConfig extends IBuilderConfig {
  wantNegativeEvolution: boolean
}

export interface IChartConfigApiModel {
  applications: {
    app_key: string
    device_type: DeviceType
  }[]
  column: number
  created_at: number
  created_by: string
  feature_name?: string
  force_kpi_field?: string
  graph_config_id: string
  how?: string
  line: number
  mode: Mode
  nb_last_days_to_ignore: number
  page?: string
  position: number
  related_glossary_items: string[]
  required_breakdown?: string
  section?: string
  series: {
    default_breakdown?: string
    graph_type: string
    min_nbu: number
    normalized_by: string[]
    values: string[]
    with_inf: boolean
    with_sup: boolean
    with_trend: boolean
    y_axes_name: string
  }[]
  title: string
  updated_at: number
  updated_by: string
  want_negative_evolution: boolean
  what?: string
  y_axes: {
    name: string
    precision: number
    unit: Unit
  }[]
}

export interface IChartConfigApiModelPayload
  extends Omit<IChartConfigApiModel, 'graph_config_id' | 'created_at' | 'created_by' | 'updated_at' | 'updated_by'> {}

export const featureNames = [
  'analytics_aggregated',
  'business_reports_ads',
  'business_reports_ua',
  'business_reports_sales',
  'business_reports_downloads',
]

type TFeatureName = (typeof featureNames)[number]

export interface IGetFieldsPayload {
  appKey?: string
  featureName: TFeatureName
}
